import React, { useState, useEffect, useRef } from 'react';
import { Send, RefreshCw, Mail, MessageSquare, CornerDownRight, AlertCircle, Check } from 'lucide-react';
import api from '../../services/api';

// Client Updates tab: a per-case, chronological, two-way, append-only
// communication history. Staff updates and portal client messages interleave;
// replying posts back to the same feed and clears the client message's
// needs-response state. Nothing is rewritten — this is a record of communication.
const ClientUpdatePanel = ({ caseId }) => {
  const [feed, setFeed] = useState([]);
  const [needsResponse, setNeedsResponse] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [content, setContent] = useState('');
  const [emailClient, setEmailClient] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [posting, setPosting] = useState(false);
  const bottomRef = useRef(null);

  const load = async (scroll = false) => {
    try {
      setLoading(true);
      setError(null);
      const resp = await api.get(`/cases/${caseId}/feed`);
      setFeed(resp.data.feed || []);
      setNeedsResponse(resp.data.needs_response || 0);
      if (scroll) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load the communication feed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [caseId]);

  const handlePost = async () => {
    if (!content.trim()) return;
    try {
      setPosting(true);
      await api.post(`/cases/${caseId}/feed`, {
        content: content.trim(),
        email_client: emailClient,
        parent_message_id: replyTo?.id || null,
      });
      setContent('');
      setReplyTo(null);
      await load(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const handleMarkRead = async () => {
    try {
      await api.post(`/cases/${caseId}/feed/mark-read`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to mark read');
    }
  };

  const deliveryBadge = (item) => {
    if (item.from_client) return null;
    if (item.email_status === 'sent') return <span className="text-green-400" title="Email relay-accepted"><Mail size={12} className="inline" /> emailed</span>;
    if (item.email_status === 'failed') return <span className="text-red-400" title="Email failed to send"><AlertCircle size={12} className="inline" /> email failed</span>;
    if (item.email_status === 'pending') return <span className="text-amber-400"><Mail size={12} className="inline" /> emailing…</span>;
    return <span className="text-virgo-teal-500"><MessageSquare size={12} className="inline" /> portal only</span>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-virgo-teal-400" /></div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
          <p className="text-red-300 mb-3">{error}</p>
          <button onClick={() => load()} className="px-4 py-2 text-sm bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-white">Client Communication</h3>
          {needsResponse > 0 && (
            <span className="px-2 py-1 text-xs rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
              {needsResponse} awaiting response
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {needsResponse > 0 && (
            <button onClick={handleMarkRead} className="px-3 py-1.5 text-xs bg-virgo-navy-600 text-virgo-teal-300 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 flex items-center gap-1">
              <Check size={12} /> Mark all read
            </button>
          )}
          <button onClick={() => load()} className="p-2 text-virgo-teal-400 hover:text-white" title="Refresh"><RefreshCw size={16} /></button>
        </div>
      </div>

      {/* Feed */}
      {feed.length === 0 ? (
        <div className="text-center py-10 bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl text-virgo-teal-400 mb-4">
          No communication yet. Post the first update below.
        </div>
      ) : (
        <div className="space-y-3 mb-4 max-h-[46vh] overflow-y-auto pr-1">
          {feed.map((item) => (
            <div
              key={item.id}
              className={`rounded-xl border p-4 ${
                item.from_client
                  ? `bg-virgo-navy-800/70 border-cyan-500/30 ${item.needs_response ? 'ring-1 ring-amber-500/40' : ''}`
                  : 'bg-virgo-navy-700/40 border-virgo-teal-700/30 ml-6'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${item.from_client ? 'bg-cyan-500/20 text-cyan-300' : 'bg-virgo-navy-600 text-virgo-teal-300'}`}>
                    {item.from_client ? 'Client' : 'Staff'}
                  </span>
                  <span className="text-sm font-medium text-white">{item.author || (item.from_client ? 'Client' : 'Staff')}</span>
                  {item.needs_response && <span className="text-xs text-amber-300">• needs response</span>}
                </div>
                <span className="text-xs text-virgo-teal-500">{item.created_at ? new Date(item.created_at).toLocaleString() : ''}</span>
              </div>
              {item.subject && <p className="text-sm font-semibold text-virgo-teal-200 mb-1">{item.subject}</p>}
              <p className="text-sm text-virgo-teal-100 whitespace-pre-wrap">{item.content}</p>
              <div className="flex items-center justify-between mt-2 text-xs">
                <span>{deliveryBadge(item)}</span>
                {item.from_client && (
                  <button onClick={() => { setReplyTo(item); bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
                          className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                    <CornerDownRight size={12} /> Reply
                  </button>
                )}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Composer */}
      <div className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl p-4">
        {replyTo && (
          <div className="flex items-center justify-between mb-2 text-xs text-virgo-teal-400">
            <span className="truncate">Replying to: “{replyTo.content.slice(0, 80)}”</span>
            <button onClick={() => setReplyTo(null)} className="text-virgo-teal-500 hover:text-white ml-2">✕</button>
          </div>
        )}
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          placeholder="Post an update to the client…"
          className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 resize-none"
        />
        <div className="flex items-center justify-between mt-2">
          <label className="flex items-center gap-2 text-sm text-virgo-teal-300 cursor-pointer">
            <input type="checkbox" checked={emailClient} onChange={(e) => setEmailClient(e.target.checked)} className="h-4 w-4" />
            Also email the client
          </label>
          <button
            onClick={handlePost}
            disabled={posting || !content.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 flex items-center gap-1"
          >
            <Send size={14} /> {posting ? 'Posting…' : replyTo ? 'Send reply' : 'Post update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientUpdatePanel;
